import React, { useState, useEffect, useMemo } from 'react';
import WeatherChart from './WeatherChart';
import TabSelector from '../components/TabSelector';
import { CHART_CONFIGS } from '../misc/chartConfigs';
import '../styles/ExpandableCard.css';
import IconTempLite from '../assets/icon-temp-lite.png';
import IconRainLite from '../assets/icon-rain-lite.png';
import IconWindLite from '../assets/icon-wind-lite.png';
import IconCloudLite from '../assets/icon-cloud-lite.png';
import IconTrash from '../assets/trash-svgrepo-com.svg';

const ExpandableCard = ({ venue, forecastData, unitSettings, onRemove }) => {
  const [selectedDate, setSelectedDate] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('temperature');

  useEffect(() => {
    if (venue.date) {
      setSelectedDate(venue.date);
    } else if (forecastData.length) {
      const d = new Date(Number(forecastData[0].forecast_date));
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      setSelectedDate(`${yyyy}-${mm}-${dd}`);
    }
  }, [venue, forecastData]);

  const formatLocalDate = ts => {
    const d = new Date(Number(ts));
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const filteredData = useMemo(() =>
    forecastData.filter(d => formatLocalDate(d.forecast_date) === selectedDate),
  [selectedDate, forecastData]);

  const formattedData = useMemo(() =>
    filteredData.map(dp => ({
      date: new Date(Number(dp.forecast_date)).getTime(),
      temperature:     +dp.temperature     || 0,
      temperatureMin:  +dp.temperatureMin  || 0,
      temperatureMax:  +dp.temperatureMax  || 0,
      precipitation:   +dp.precipitation   || 0,
      cloud:           +dp.cloud           || 0,
      windSpeed:       +dp.windSpeed       || 0,
      windDirection:   +dp.windDirection   || 0,
      watts:           +dp.watts           || 0,
      wattsMin:        +dp.wattsMin        || 0,
      wattsMax:        +dp.wattsMax        || 0,
      rh2m:            +dp.rh2m            || 0,
      rh2mMin:         +dp.rh2mMin         || 0,
      rh2mMax:         +dp.rh2mMax         || 0,
    })),
  [filteredData]);

  const insights = useMemo(() => {
    if (!formattedData.length) return null;
    const maxTemp = Math.max(...formattedData.map(d => d.temperatureMax));
    const minTemp = Math.min(...formattedData.map(d => d.temperatureMin));
    const totalPrecip = formattedData.reduce((s,d)=>s+d.precipitation,0);
    const precipCount = formattedData.filter(d=>d.precipitation>0).length;
    const probRain = (precipCount/formattedData.length)*100;
    const avgWind = formattedData.reduce((s,d)=>s+d.windSpeed,0)/formattedData.length;
    const avgCloud= formattedData.reduce((s,d)=>s+d.cloud,0)/formattedData.length;
    return { maxTemp, minTemp, totalPrecip, probRain, avgWind, avgCloud };
  }, [formattedData]);

  const [y, mo, da] = selectedDate.split('-').map(Number);
  const startDateObj = new Date(y, mo - 1, da);
  const endDateObj   = new Date(y, mo - 1, da, 23, 59, 59, 999);

  return (
    <div className="expandable-card">
      <div className="card-toolbar">
        <span
          className="tooltip"
          data-tooltip="Removing this card is irreversible"
        >
          <button
            className="toolbar-icon"
            onClick={e => {
              e.stopPropagation();
              onRemove(venue.name, selectedDate);
            }}
            aria-label="Remove this card"
            title="Remove this card"
          >
            <img src={IconTrash} alt="" />
          </button>
        </span>
      </div>

      <div className="card-header" onClick={() => setExpanded(e => !e)}>
        <div className="header-content">
          <h3>{venue.name}</h3>
          <p>
            {selectedDate
              ? new Date(`${selectedDate}T00:00:00`).toLocaleDateString()
              : 'Select a date'}
          </p>
        </div>
        <button
          className="toggle-button"
          onClick={e => { e.stopPropagation(); setExpanded(e => !e); }}
        >
          {expanded ? 'Collapse' : 'Expand'}
        </button>
      </div>

      {expanded && (
        <div className="card-body flex-row">
          <div className="chart-column">
            <div className="static-date-container">
              <strong>Date:</strong> {selectedDate}
            </div>
            <TabSelector
              activeTab={activeTab}
              onTabChange={tabId => setActiveTab(tabId)}
            />
            <div className="chart-container">
              {formattedData.length ? (
                <WeatherChart
                  data={formattedData}
                  startDate={startDateObj}
                  endDate={endDateObj}
                  interval={0}
                  unitSettings={unitSettings}
                  chartConfig={CHART_CONFIGS[activeTab]}
                  calculateDomain={(data, minKey, maxKey, margin) => {
                    const mins = data.map(i => i[minKey]);
                    const maxs = data.map(i => i[maxKey]);
                    return [Math.min(...mins) - margin, Math.max(...maxs) + margin];
                  }}
                />
              ) : (
                <p>No data for this date.</p>
              )}
            </div>
          </div>

          {insights && (
            <div className="insights-sidebar">
              <div className="insight-item">
                <img src={IconTempLite}  alt="Max Temp"      className="insight-icon" />
                <p className="insight-value">
                  {insights.maxTemp.toFixed(2)}°{unitSettings.t2m_units}
                </p>
                <p className="insight-label">Max Temp</p>
              </div>
              <div className="insight-item">
                <img src={IconTempLite}  alt="Min Temp"      className="insight-icon" />
                <p className="insight-value">
                  {insights.minTemp.toFixed(2)}°{unitSettings.t2m_units}
                </p>
                <p className="insight-label">Min Temp</p>
              </div>
              <div className="insight-item">
                <img src={IconRainLite}  alt="Precipitation" className="insight-icon" />
                <p className="insight-value">
                  {insights.totalPrecip.toFixed(2)} {unitSettings.precipitation_units}
                </p>
                <p className="insight-label">Total Precip</p>
                <p className="insight-value">
                  {insights.probRain.toFixed(0)}%
                </p>
                <p className="insight-label">Rain Prob.</p>
              </div>
              <div className="insight-item">
                <img src={IconWindLite}  alt="Avg Wind"      className="insight-icon" />
                <p className="insight-value">
                  {insights.avgWind.toFixed(2)} {unitSettings.ws2m_units}
                </p>
                <p className="insight-label">Avg Wind</p>
              </div>
              <div className="insight-item">
                <img src={IconCloudLite} alt="Cloud Cover"   className="insight-icon" />
                <p className="insight-value">
                  {insights.avgCloud.toFixed(0)}%
                </p>
                <p className="insight-label">Cloud Cover</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExpandableCard;